// vue
import { h, Slots } from 'vue';

// utils
import { getStrLen } from '@/utils';

/**
 * 単体コード記法（`code`）か判定
 * @params {String} 判定対象の文字列
 * @params {String} コード記法の初めか終わりのどちらを判定するか指定
 * @returns {Boolean} コード記法の場合はtrue
 */
const isCode = (txt: string, type: 'start' | 'end' = 'start') => {
  if (getStrLen(txt) <= 1) return false;
  const last2char = txt.slice(-2);

  let regex = new RegExp('`[^`]+');
  if (type === 'end') {
    regex = new RegExp('[^`]+`');
  }
  return regex.test(last2char);
};

/**
 * コードブロック記法（```code block```）か判定
 * @params {String} 判定対象の文字列
 * @returns {Boolean} コードブロック記法の場合はtrue
 */
const isCodeBlock = (txt: string) => {
  if (getStrLen(txt) <= 2) return false;
  return txt.slice(-3) === '```';
};

/**
 * 引数で受け取った1文字が改行コードかどうか判定
 * @param char
 * @returns true / false
 */
export const isLinefeedCode = (char: string) => {
  if (char === '\n') return true;
  if (char === '\r') return true;
  if (char === '\r\n') return true;
  return false;
};

const isURL = (txt: string) => {
  if (getStrLen(txt) <= 3) return false;
  return txt.slice(-4) === 'http';
};

const isURLEnd = (txt: string) => {
  if (txt.length === 0) return false;
  const urlEndPattern = [' ', '　', '\t', '\n', '\r', '\r\n'];
  for (const endPattern of urlEndPattern) {
    if (txt === endPattern) return true;
  }

  // 全角
  const isJapanese = /^[^\x20-\x7e]*$/;
  if (txt.match(isJapanese)) return true;

  return false;
};

const isQuotationMark = (txt: string) => {
  if (getStrLen(txt) < 2) return false;

  return txt.slice(-2) === '> ';
};

/**
 * 受け取ったURLを外部リンクタグにして返す
 * @param url リンクタグに設定したいURL
 * @returns <a href="url" target="_blank" rel="nofollow noopener noreferrer">url</a>
 */
export const createLinkTag = (url: string) => {
  const attr = {
    href: url,
    target: '_blank',
    rel: 'nofollow noopener noreferrer'
  };

  return h('a', attr, [url]);
};

export const getSlotsMsgBody = (slots: Slots) => {
  // デフォルトスロットが取れない場合
  if (typeof slots.default !== 'function') {
    return -1;
  }

  const msgVNode = slots.default();
  const msgBody = msgVNode[0]?.children || '';

  // VNodeArrayChildren or RawSlots のとき
  if (typeof msgBody !== 'string') {
    return -1;
  }

  return msgBody;
};

/**
 * 特殊記法をHTMLタグに変換して配列にして返す
 * @param msgBody 特殊記法変換前の本文
 * @returns 特殊記法変換後の配列
 */
export const generateHTML = (msgBody: string) => {
  const htmlList: any = [];
  let isQuotationMarkMode = false;
  // 引用符の中でインラインコードを書く用
  const tmpQuoteList = [];
  let isURLMode = false;
  let isCodeBlockMode = false;
  let isCodeMode = false;
  let txt = '';

  for (let i = 0; i < getStrLen(msgBody); i++) {
    const char1 = msgBody[i];
    txt += char1;

    // 引用符
    // ================================
    if (isQuotationMark(txt) && !isCodeBlockMode) {
      isQuotationMarkMode = true;
      const beforeTxt = txt.slice(0, getStrLen(txt) - 2);
      if (beforeTxt) {
        htmlList.push(beforeTxt);
      }
      txt = '';
    }

    if (isQuotationMarkMode && isLinefeedCode(txt.slice(-1))) {
      const quote = txt.slice(0, -1);
      if (tmpQuoteList.length > 1) {
        tmpQuoteList.push(quote);
        htmlList.push(
          h('blockquote', { class: 'quotation-mark' }, [...tmpQuoteList])
        );

        tmpQuoteList.length = 0;
      } else {
        htmlList.push(h('blockquote', { class: 'quotation-mark' }, [quote]));
      }
      txt = '';
      isQuotationMarkMode = false;
    }

    // URL
    // ================================
    if ((!isCodeMode && !isCodeBlockMode && isURL(txt)) || isURLMode) {
      // 今までの文字をhtmlListに追加
      if (isURL(txt)) {
        // コード開始直前のテキストがあればpush
        const beforeTxt = txt.slice(0, getStrLen(txt) - 4);
        if (beforeTxt) {
          htmlList.push(beforeTxt);
        }
        txt = txt.slice(-4);
        isURLMode = true;
      }

      // URLの終わり
      if (isURLEnd(char1)) {
        const url = txt.slice(0, getStrLen(txt) - 1);
        htmlList.push(createLinkTag(url));

        isURLMode = false;
        txt = txt[getStrLen(txt) - 1];
      }
    }

    // コード
    // ================================

    // コード開始
    if (isCode(txt)) {
      // 今までの文字をhtmlListに追加してコードスタート
      const lastChar = txt.length > 1 ? txt.slice(-1) : '';

      // コード開始直前のテキストがあればpush
      const beforeTxt = txt.slice(0, getStrLen(txt) - 2);
      if (beforeTxt) {
        if (isQuotationMarkMode) {
          tmpQuoteList.push(beforeTxt);
        } else {
          htmlList.push(beforeTxt);
        }
      }
      txt = lastChar;
      isCodeMode = true;
    }
    // コード終了
    if (isCodeMode) {
      if (isCode(txt, 'end')) {
        const code = txt.slice(0, -1);
        if (isQuotationMarkMode) {
          tmpQuoteList.push(h('code', [code]));
        } else {
          htmlList.push(h('code', [code]));
        }
        txt = '';
        isCodeMode = false;
      }
    }

    // コードブロック
    // ================================
    // 今までの文字をhtmlListに追加してコードブロックスタート
    if (isCodeBlock(txt) && !isCodeBlockMode) {
      // コード開始直前のテキストがあればpush
      const beforeTxt = txt.slice(0, getStrLen(txt) - 3);
      if (beforeTxt) {
        htmlList.push(beforeTxt);
      }
      txt = '';
      isCodeBlockMode = true;
    }

    // コードブロックの終わり
    if (isCodeBlock(txt) && isCodeBlock) {
      let code = txt.replace('```', '');
      // コードブロック終了直前が改行コードの場合は本文に含めない
      if (isLinefeedCode(code[0])) code = code.slice(1, getStrLen(code));
      htmlList.push(h('pre', [code]));

      isCodeBlockMode = false;
      txt = '';
    }
  }

  // URLモードがtrueのままループを抜けた = URL単体のメッセージのとき
  if (isURLMode) {
    const url = txt.replace(' ', '').replace('　', '');
    htmlList.push(createLinkTag(url));
  } else if (isQuotationMarkMode) {
    htmlList.push(
      h('blockquote', { class: 'quotation-mark' }, [...tmpQuoteList, txt])
    );
  } else if (txt.length > 0) {
    htmlList.push(txt);
  }

  return htmlList;
};
